import React from 'react';
import { Globe, Users, Languages } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../i18n';

const Countries = () => {
  const { t } = useLanguage();

  return (
    <section id="countries" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="text-[#E9782A]" size={32} />
          </div>
          <span className="text-[#E9782A] font-semibold text-sm uppercase tracking-wider">
            {t.countries.label}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            {t.countries.titlePre}{' '}
            <span className="text-[#E9782A]">{t.countries.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-600">
            {t.countries.subtitle}
          </p>
        </div>

        {/* Countries Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {t.countries.list.map((country, index) => (
            <Card
              key={country.flag}
              className="group hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 border-2 border-transparent hover:border-[#E9782A]/20 bg-gradient-to-br from-white to-gray-50 animate-in fade-in zoom-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <CardContent className="p-6 text-center">
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {country.flag}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#E9782A] transition-colors duration-300">
                  {country.name}
                </h3>
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <Users size={16} className="text-[#E9782A]" />
                  <span className="text-sm font-semibold">{country.influencers}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Languages Bar */}
        <div className="mb-12 text-center" data-testid="languages-section">
          <div className="inline-flex items-center space-x-2 mb-6">
            <Languages className="text-[#E9782A]" size={24} />
            <h3 className="text-xl font-bold text-gray-900">
              {t.countries.languagesTitle}
            </h3>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {t.countries.languages.map((language) => (
              <Badge
                key={language}
                variant="outline"
                className="border-[#E9782A]/40 text-gray-700 px-4 py-2 text-sm font-medium hover:bg-[#E9782A]/10 transition-colors"
              >
                {language}
              </Badge>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-gradient-to-r from-[#E9782A] to-[#d96820] rounded-2xl p-8 text-white shadow-2xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2" data-testid="countries-influencers-stat">133</div>
              <div className="text-sm opacity-90">{t.countries.statInfluencers}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">45M+</div>
              <div className="text-sm opacity-90">{t.countries.statReach}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">250+</div>
              <div className="text-sm opacity-90">{t.countries.statCampaigns}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-sm opacity-90">{t.countries.statSatisfaction}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Countries;
